import { buildField, getFieldValueIfDirty, updateTabField } from '../../common/utils';
import { SUBCATEGORIES } from '../../common/categories';
import { LICENSE_FIELDS, MOBILE_MESSAGING_SERVICES_FIELDS, COMPANY_INFO_FIELDS } from '../../common/fields';
import * as actions from './licensingActions';
import { commonActionsReducer } from '../../common/actions';
import { SYSTEM_MODES } from '@silverfort-nodelibs/common/utils/system-mode-config';

const { LICENSE, MOBILE_MESSAGING_SERVICES } = SUBCATEGORIES;
const { LICENSE_KEY, LICENSE_SYNC_TIME, LICENSE_UPDATE_TIME } = LICENSE_FIELDS;
const { COMPANY_NAME } = COMPANY_INFO_FIELDS;
const { ADMIN_CONSOLE_SENDERS, ADMIN_CONSOLE_RECEIVERS, CONNECTION_STRING } = MOBILE_MESSAGING_SERVICES_FIELDS;

export const fieldsReducer = (state, action) => {
    let updatedCsState = state;
    switch (action.type) {
        case actions.UPDATE_LICENSE:
            const { licenseDetails, senderConnectionStrings, receiverConnectionStrings } = action.payload;
            if (!licenseDetails || !licenseDetails.licenseKey) {
                return state;
            }
            let updatedState = updateTabField(state, LICENSE.ID, LICENSE_KEY.ID, licenseDetails.licenseKey);
            updatedState[LICENSE.ID].fields[LICENSE_KEY.ID].licenseDetails = licenseDetails;
            const hasServiceBus = senderConnectionStrings?.length > 0 || receiverConnectionStrings?.length > 0;

            if (hasServiceBus) {
                updatedState[MOBILE_MESSAGING_SERVICES.ID] = {
                    id: MOBILE_MESSAGING_SERVICES.ID,
                    name: MOBILE_MESSAGING_SERVICES.NAME,
                    fields: {
                        [ADMIN_CONSOLE_SENDERS.ID]: buildField(
                            ADMIN_CONSOLE_SENDERS,
                            mapToConnectionStringFields(senderConnectionStrings || [])
                        ),
                        [ADMIN_CONSOLE_RECEIVERS.ID]: buildField(
                            ADMIN_CONSOLE_RECEIVERS,
                            mapToConnectionStringFields(receiverConnectionStrings || [])
                        )
                    },
                    validators: {}
                };
            } else {
                if (updatedState[MOBILE_MESSAGING_SERVICES.ID]) {
                    const { [MOBILE_MESSAGING_SERVICES.ID]: removed, ...rest } = updatedState;
                    updatedState = rest;
                }
            }

            if (
                licenseDetails.companyName &&
                updatedState[LICENSE.ID] &&
                updatedState[LICENSE.ID].fields[COMPANY_NAME.ID] &&
                action.payload.systemMode === SYSTEM_MODES.SAAS
            ) {
                const currentCompanyName = updatedState[LICENSE.ID].fields[COMPANY_NAME.ID].value;
                if (!currentCompanyName || currentCompanyName.toLowerCase() === 'acme') {
                    updatedState = updateTabField(
                        updatedState,
                        LICENSE.ID,
                        COMPANY_NAME.ID,
                        licenseDetails.companyName
                    );
                }
            }

            return updatedState;
        case actions.UPDATE_COMPANY_NAME:
            return updateTabField(state, LICENSE.ID, action.payload.fieldId, action.payload.value);
        case actions.REFRESH_CONNECTION_STRINGS_SENT:
            if (!state[MOBILE_MESSAGING_SERVICES.ID]) {
                return updatedCsState;
            }
            const nodes = Object.keys(state[MOBILE_MESSAGING_SERVICES.ID].fields).filter(
                (fId) => fId !== ADMIN_CONSOLE_SENDERS.ID && fId !== ADMIN_CONSOLE_RECEIVERS.ID
            );
            for (const nodeId of nodes) {
                const currValue = updatedCsState[MOBILE_MESSAGING_SERVICES.ID].fields[nodeId].value;
                updatedCsState = updateTabField(updatedCsState, MOBILE_MESSAGING_SERVICES.ID, nodeId, currValue, false);
                updatedCsState[MOBILE_MESSAGING_SERVICES.ID].fields[nodeId].loading = true;
            }
            return updatedCsState;
        case actions.UPDATE_NODES_CS:
            if (!state[MOBILE_MESSAGING_SERVICES.ID]) {
                return updatedCsState;
            }
            const { connectionStrings, error } = action.payload;
            const nodeIds = Object.keys(state[MOBILE_MESSAGING_SERVICES.ID].fields).filter(
                (fId) => fId !== ADMIN_CONSOLE_SENDERS.ID && fId !== ADMIN_CONSOLE_RECEIVERS.ID
            );
            updatedCsState[MOBILE_MESSAGING_SERVICES.ID].fields[ADMIN_CONSOLE_RECEIVERS.ID].error = error;

            for (const nodeId of nodeIds) {
                const currValue = updatedCsState[MOBILE_MESSAGING_SERVICES.ID].fields[nodeId].value;
                if (!connectionStrings[nodeId]) {
                    updatedCsState = updateTabField(
                        updatedCsState,
                        MOBILE_MESSAGING_SERVICES.ID,
                        nodeId,
                        currValue,
                        false
                    );
                    updatedCsState[MOBILE_MESSAGING_SERVICES.ID].fields[
                        nodeId
                    ].error = `Failed to refresh connection strings for ${
                        updatedCsState[MOBILE_MESSAGING_SERVICES.ID].fields[nodeId].name
                    }`;
                } else {
                    updatedCsState = updateTabField(
                        updatedCsState,
                        MOBILE_MESSAGING_SERVICES.ID,
                        nodeId,
                        mapToConnectionStringFields(connectionStrings[nodeId].map((cs) => ({ connString: cs }))),
                        false
                    );
                }
                updatedCsState[MOBILE_MESSAGING_SERVICES.ID].fields[nodeId].loading = false;
            }
            return updatedCsState;
        default:
            return commonActionsReducer(state, action, initFieldsState);
    }
};

export const initFieldsState = ({ settings }) => {
    const systemMode = settings.systemMode || SYSTEM_MODES.ON_PREM;
    const isCompanyNameRequired = systemMode === SYSTEM_MODES.ON_PREM;
    const hasServiceBus =
        settings.RavenSendConnectionStrings?.length > 0 || settings.RavenReceiveConnectionStrings?.length > 0;

    const state = {
        [LICENSE.ID]: {
            id: LICENSE.ID,
            name: LICENSE.NAME,
            fields: {
                [COMPANY_NAME.ID]: buildField(COMPANY_NAME, settings.CompanyName, { required: isCompanyNameRequired }),
                [LICENSE_KEY.ID]: {
                    ...buildField(LICENSE_KEY, settings.License.licenseKey),
                    licenseDetails: settings.License
                },
                [LICENSE_UPDATE_TIME.ID]: buildField(LICENSE_UPDATE_TIME, settings.LicenseLastUpdateTime),
                [LICENSE_SYNC_TIME.ID]: buildField(LICENSE_SYNC_TIME, settings.LicenseLastSyncTime)
            },
            validators: {}
        }
    };

    if (hasServiceBus) {
        state[MOBILE_MESSAGING_SERVICES.ID] = {
            id: MOBILE_MESSAGING_SERVICES.ID,
            name: MOBILE_MESSAGING_SERVICES.NAME,
            fields: {
                [ADMIN_CONSOLE_SENDERS.ID]: buildField(
                    ADMIN_CONSOLE_SENDERS,
                    mapToConnectionStringFields(settings.RavenSendConnectionStrings || [])
                ),
                [ADMIN_CONSOLE_RECEIVERS.ID]: buildField(
                    ADMIN_CONSOLE_RECEIVERS,
                    mapToConnectionStringFields(settings.RavenReceiveConnectionStrings || [])
                )
            },
            validators: {}
        };

        const nodesConfig = settings.NodesConfigurations;
        const sortedNodes = Object.keys(nodesConfig).sort();

        for (const nodeId of sortedNodes) {
            const nodeConfig = nodesConfig[nodeId];
            state[MOBILE_MESSAGING_SERVICES.ID].fields[nodeId] = {
                ...buildField(
                    CONNECTION_STRING,
                    mapToConnectionStringFields(nodeConfig.RavenReceiveConnectionStrings || [])
                ),
                id: nodeId,
                name: nodeConfig.Name
            };
        }
    }

    return state;
};

export const mapToSettings = (saveAll, selectedTab, fields) => {
    const newSettings = {};
    if (saveAll || selectedTab === LICENSE.ID) {
        const licenseFields = fields[LICENSE.ID].fields;
        newSettings.CompanyName = getFieldValueIfDirty(licenseFields[COMPANY_NAME.ID]);
        newSettings.License = getFieldValueIfDirty(
            licenseFields[LICENSE_KEY.ID],
            licenseFields[LICENSE_KEY.ID].licenseDetails
        );
    }
    return newSettings;
};

function mapToConnectionStringFields(connectionStrings) {
    const connectionStringsFields = [];
    if (!connectionStrings || !Array.isArray(connectionStrings)) {
        return connectionStringsFields;
    }
    for (const connectionString of connectionStrings) {
        connectionStringsFields.push({
            [CONNECTION_STRING.ID]: buildField(CONNECTION_STRING, connectionString.connString)
        });
    }
    return connectionStringsFields;
}
